// R10.7 — client-side Add-to-Calendar. Generates an .ics file + a Google
// Calendar template URL from a confirmed booking, entirely in the browser (no
// server round-trip). Times are emitted in UTC (the `Z` form), which every
// calendar app interprets correctly regardless of the guest's locale.

export interface CalendarEvent {
  title: string;
  /** ISO 8601 start instant (e.g. the reservation slot start). */
  start: string;
  /** ISO 8601 end instant. When absent, defaults to start + 90 min. */
  end?: string;
  description?: string;
  location?: string;
  /** Stable id for the VEVENT UID (e.g. the confirmation number). */
  uid?: string;
}

const DEFAULT_DURATION_MS = 90 * 60 * 1000;

/** Format an ISO instant as an iCal/Google UTC stamp: YYYYMMDDTHHMMSSZ. */
export function toICalUTC(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function resolveEnd(ev: CalendarEvent): string {
  if (ev.end) return ev.end;
  const start = new Date(ev.start);
  return new Date(start.getTime() + DEFAULT_DURATION_MS).toISOString();
}

/** Escape a value for an iCalendar text field (RFC 5545 §3.3.11). */
function icalEscape(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

/** Build a complete VCALENDAR document for a single booking. */
export function buildICS(ev: CalendarEvent): string {
  const dtStart = toICalUTC(ev.start);
  const dtEnd = toICalUTC(resolveEnd(ev));
  const uid = `${ev.uid || dtStart || Date.now()}@yeboguest`;
  const stamp = toICalUTC(new Date().toISOString());

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//YeboGuest//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${icalEscape(ev.title)}`,
    ev.description ? `DESCRIPTION:${icalEscape(ev.description)}` : '',
    ev.location ? `LOCATION:${icalEscape(ev.location)}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  // RFC 5545 mandates CRLF line endings.
  return lines.join('\r\n');
}

/** Build a Google Calendar "create event" template URL. */
export function buildGoogleCalendarUrl(ev: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: ev.title,
    dates: `${toICalUTC(ev.start)}/${toICalUTC(resolveEnd(ev))}`,
  });
  if (ev.description) params.set('details', ev.description);
  if (ev.location) params.set('location', ev.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Trigger a download of the .ics for an event. Browser-only — guarded so it is a
 * no-op in non-DOM environments (e.g. unit tests / SSR). Returns true if the
 * download was initiated.
 */
export function downloadICS(ev: CalendarEvent, filename = 'reservation.ics'): boolean {
  if (typeof document === 'undefined' || typeof URL?.createObjectURL !== 'function') return false;
  const blob = new Blob([buildICS(ev)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the click a tick before revoking, then clean up.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
}
